/* istanbul ignore file */
// @ts-check
import { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import _ from 'lodash';
import Text from 'components/base/Text';
import { Flex, WhiteSection, Margin } from 'components/base/layout-components';
import { SPACES } from 'common/constants/sizes';
import { createConfirmModalOptions } from 'modals/modal-options-factory';
import * as LighthouseService from 'common/services/lighthouse.service';
import useSettingsService from 'components/hooks/useSettingsService';
import { Tooltip } from '@material-ui/core';
import Icon from 'components/base/Icon';
import { SettingValues } from '@linearb/linjs-common';
import { LBButton } from '@linearb/buzz';

const tooltipContent = () => (
  <Flex direction='column' padding='0.5rem' height='7rem' overflow='auto'>
    <Flex pt='0.2rem'>
      <Icon name='alert' color='dodgerBlue' />
      <Text pl='0.7rem' pb='0.5rem' weight='600' size='medium'>
        Initiates 30 day backfill
      </Text>
    </Flex>
    <Text pb='0.5rem' pt='1rem' size='medium' color='shuttleGray'>
      All connected branches will be
    </Text>
    <Text pb='0.5rem' pt='0.2rem' size='medium' color='shuttleGray'>
      refreshed with data.
    </Text>
  </Flex>
);

export default function withSetting(WrappedComponent) {
  return function (props) {
    let { customOptions = {} } = props;
    const {
      name,
      value,
      entityType,
      entityId,
      definitionId,
      label,
      subtext,
      toggleModal,
      defaultValue,
      allCustomOptions = {},
      isApiTokensEnabled = false,
      isReportedRelease,
      useEffectValidationFN,
      title = 'Notice!',
      additionalModalDisplayCheck,
      descriptionTextWidth = null,
      buttonsContainerFlexDirection,
      disabled = false
    } = props;
    const [stateValue, setStateValue] = useState(value);
    const [hasChanged, setHasChanged] = useState(false);
    const [wasReset, setWasReset] = useState(false);
    const { updateSetting } = useSettingsService();
    const saveSettings = async (value = null) => {
      updateSetting(name, value, entityType, entityId, true);
      setHasChanged(false);
    };
    const settingWithTooltips = [
      SettingValues.EXCLUDE_BRANCHES,
      SettingValues.EXCLUDE_BY_TARGET_BRANCHES
    ];
    const showTooltip = settingWithTooltips.includes(name);

    customOptions = _.get(
      allCustomOptions,
      `${stateValue?.key}`,
      customOptions
    );

    /* istanbul ignore next */
    const handleSave = async () => {
      if (
        customOptions?.confirmation_modal &&
        (!additionalModalDisplayCheck ||
          additionalModalDisplayCheck({ stateValue, value }))
      ) {
        toggleModal(
          createConfirmModalOptions({
            title,
            description: customOptions.confirmation_modal.confirmation_text,
            CheckboxCheckedNote: customOptions.confirmation_modal.checkbox
              ? customOptions.confirmation_modal.checkbox.CheckboxCheckedNote
              : null,
            confirmationModalOptions: customOptions.confirmation_modal,
            confirmText: 'Confirm',
            cancelText: 'Cancel',
            buttonsContainerFlexDirection,
            width: customOptions.confirmation_modal.modalWidth,
            height: customOptions.confirmation_modal.modalHeight,
            descriptionTextWidth,
            onConfirm: async customPayload => {
              toggleModal();
              saveSettings({
                ...stateValue,
                ...(customPayload ? customPayload : {})
              });
            },
            onCancel: () => toggleModal(),
            size: 'medium'
          })
        );
      } else {
        saveSettings(stateValue);
      }
    };

    /* istanbul ignore next */
    const handleResetDefault = async () => {
      toggleModal(
        createConfirmModalOptions({
          title: 'Restore default',
          description: `Are you sure you want to reset '${label}' to the default value?`,
          confirmText: 'Confirm',
          cancelText: 'Cancel',
          onConfirm: async () => {
            try {
              saveSettings();

              setStateValue(defaultValue);
              setWasReset(true);

              toggleModal();
            } catch (e) {}
          },
          onCancel: () => toggleModal()
        })
      );
    };

    useEffect(() => {
      setHasChanged(useEffectValidationFN(value, stateValue));
    }, [stateValue]);

    return (
      <WhiteSection
        key={definitionId}
        t={SPACES['medium']}
        data-cy={`settings-${name}`}
      >
        <Flex align='center' direction='column' width='60%'>
          <Flex direction='column'>
            <Text size='large' color='abbey' weight='500' pb={SPACES.medium}>
              {_.toUpper(label)}
            </Text>
            <Grid container spacing={1}>
              <Grid item>
                <Text
                  lineHeight={1.7}
                  size='large'
                  pb={SPACES.medium}
                  color='darkGray'
                  dangerouslySetInnerHTML={{ __html: subtext }}
                />
              </Grid>
              {customOptions && customOptions.lighthouse ? (
                <Grid item>
                  <Text
                    onClick={() =>
                      LighthouseService.navigateArticle(
                        customOptions.lighthouse.code
                      )
                    }
                    cursor='pointer'
                    lineHeight={1.7}
                    size='large'
                    color='chambray'
                  >
                    {customOptions.lighthouse.description}
                  </Text>
                </Grid>
              ) : null}
            </Grid>
            <WrappedComponent
              onChange={setStateValue}
              {...props}
              value={stateValue}
              wasReset={wasReset}
              setWasReset={setWasReset}
            />
          </Flex>
          <Margin style={{ alignSelf: 'flex-end' }} t='small'>
            <Grid spacing={1} container>
              <Grid item>
                <LBButton
                  size='lg'
                  variant='outline'
                  onClick={handleResetDefault}
                  disabled={
                    disabled || (!isApiTokensEnabled && isReportedRelease)
                  }
                  data-cy={`restore-default-button${
                    label ? '-' + label.replace(/ /g, '-') : ''
                  }`}
                >
                  Restore Default
                </LBButton>
              </Grid>
              <Grid item>
                <Tooltip
                  title={tooltipContent()}
                  // tootltip only for excluded branches
                  disableHoverListener={!showTooltip}
                  interactive
                >
                  {/* the span is required, otherwise MUI will scream about a disabled button being passed to a tooltip */}
                  <span>
                    <LBButton
                      onClick={handleSave}
                      size='lg'
                      disabled={disabled || !hasChanged}
                      data-testid='save-button-settings-hoc'
                      data-cy={`save-button${
                        label ? '-' + label.replace(/ /g, '_') : ''
                      }`}
                    >
                      Save
                    </LBButton>
                  </span>
                </Tooltip>
              </Grid>
            </Grid>
          </Margin>
        </Flex>
      </WhiteSection>
    );
  };
}
